/**
 * interruptionController — the canonical, single-source-of-truth
 * interruption mechanism for the Qoryn realtime session.
 *
 * Every signal that means "the Founder wants control back NOW" funnels
 * through this controller: mic barge-in (VAD-triggered inside the SDK),
 * text submit during an active response, an explicit UI stop control,
 * a future mobile tap, a future hardware button, or an agent-initiated
 * yield. Each caller passes a distinct `reason` so diagnostics can tell
 * the triggers apart, but the resulting client-side sequence is
 * identical:
 *
 *   1. Snapshot the active response id + previous agent state.
 *   2. If the caller did NOT already invoke the SDK primitive
 *      (`sdkAlreadyInterrupted === false`), call `session.interrupt()`
 *      — the SDK's own primitive, which sends `response.cancel` +
 *      `output_audio_buffer.clear` on the data channel. Voice barge-in
 *      passes `sdkAlreadyInterrupted: true` because the SDK's internal
 *      VAD already fired the primitive before emitting
 *      `audio_interrupted`; every other caller passes `false` so the
 *      primitive is invoked exactly once per interrupt.
 *   3. Save the audio element's current `.muted` state, then force
 *      `.muted = true`. This is the belt-and-suspenders that gives us
 *      near-instant audible silence: WebRTC frames already decoded and
 *      queued in the browser's audio pipeline will keep flowing for
 *      ~1s after `response.cancel` reaches the server, but
 *      `HTMLMediaElement.muted = true` is synchronous and unfightable
 *      by SDK-issued `.play()` calls.
 *   4. Emit a `session.interrupted` diagnostic with the reason,
 *      response_id, prev_agent_state, and any caller-provided extras
 *      — one event shape, every trigger.
 *
 * The complementary `releaseMuteForNewResponse` is called by the
 * session's `response.created` handler AND `audio_start` handler when
 * a NEW response id arrives / new audio frames begin (multi-signal
 * release — see rearmPlayback below). It restores the pre-interrupt
 * `.muted` value AND fires `.play()` as a defensive rearm so playback
 * survives any lingering DOM-level pause we might inherit from a
 * previous browser event.
 *
 * NOTE — WHY WE DO NOT CALL `.pause()` ON INTERRUPT (2026-08-18 fix,
 * Founder audibility regression):
 *   Earlier revisions called `audioEl.pause()` alongside the mute flip
 *   "for good measure". This turned out to be a critical bug for
 *   MediaStream-backed audio elements. For `HTMLAudioElement` whose
 *   `srcObject` is a live WebRTC `MediaStream`, calling `.pause()`
 *   stops playback and browsers DO NOT auto-resume when subsequent
 *   audio frames arrive on the same MediaStream — you MUST explicitly
 *   call `.play()` to rearm. The mute-only path (this revision) leaves
 *   playback armed; the audio element keeps consuming WebRTC frames
 *   (which the server has already cleared via `output_audio_buffer.clear`)
 *   so the pipeline stays hot and ready for the next response.
 *   Stop signals must NEVER decommission the audio pipeline — only the
 *   current response.
 *
 * Design constraints:
 *   - No new abstraction layers. A single tiny factory returning a
 *     tiny object. No providers, no reducers, no state machines.
 *   - Pure — no React deps, no hooks. This keeps it trivially
 *     unit-testable (see interruptionController.test.js).
 *   - Additive — the proven voice barge-in path becomes a caller, not
 *     a separate implementation.
 */

/**
 * @typedef {"voice_bargein" | "text_submit" | "user_stop" | "agent_yield" | "unknown"} InterruptionReason
 */

/**
 * @typedef {object} InterruptOptions
 * @property {InterruptionReason} reason - Categorical reason string.
 *   Recorded verbatim in the diagnostic; drives no branching in the
 *   canonical sequence itself (all triggers do the same thing).
 * @property {boolean} [sdkAlreadyInterrupted=false] - True when the
 *   SDK's internal VAD has already invoked `session.interrupt()` for
 *   this event. Voice barge-in passes true; every other caller passes
 *   false.
 * @property {string | null} [activeResponseId=null] - The response id
 *   we are interrupting. Recorded in the diagnostic; the controller
 *   itself does not use it for branching.
 * @property {string | null} [prevAgentState=null] - What state we were
 *   in when the interrupt fired. Recorded in the diagnostic.
 * @property {Record<string, any>} [extra] - Any additional context the
 *   caller wants surfaced in the diagnostic (text_length, etc.).
 */

/**
 * @typedef {object} InterruptionController
 * @property {(options: InterruptOptions) => void} interrupt - The one
 *   canonical entrypoint. Idempotent within a single response — safe
 *   to call multiple times.
 * @property {() => boolean} isMutedByInterrupt - True iff the last
 *   .muted transition was set by this controller (and not yet
 *   released). Used by consumers to reason about mute state without
 *   reading DOM.
 * @property {(newResponseId: string | null, options?: {onlyIfDifferent?: string | null, source?: string}) => void} releaseMuteForNewResponse
 *   - Restore the pre-interrupt `.muted` value when a new response
 *   arrives, then defensively call `.play()` to rearm playback in
 *   case a legacy or upstream code path paused the element. Callers
 *   can pass the previous response id so the release only fires when
 *   the SERVER has moved past the interrupted one. `source` is
 *   recorded on the diagnostic (e.g. "response.created" |
 *   "audio_start" | "watchdog").
 * @property {() => void} reset - Clear internal state (called on
 *   session teardown).
 */

/**
 * Snapshot the audibility-relevant fields of the audio element +
 * downstream audio graph for diagnostic breadcrumbs. Pure — never
 * throws, returns null-safe defaults.
 *
 * @param {HTMLAudioElement | null} audioEl
 * @param {AudioContext | null} audioCtx
 * @returns {Record<string, any>}
 */
export function snapshotAudioPipeline(audioEl, audioCtx = null) {
    if (!audioEl) {
        return {
            audio_el: null,
            audio_ctx_state: audioCtx?.state ?? null,
        };
    }
    let srcObjectKind = null;
    let remoteTracks = [];
    try {
        const src = audioEl.srcObject;
        if (src) {
            srcObjectKind = src.constructor?.name ?? typeof src;
            if (src && typeof src.getAudioTracks === "function") {
                remoteTracks = src.getAudioTracks().map((t) => ({
                    kind: t.kind,
                    enabled: t.enabled,
                    muted: t.muted,
                    ready_state: t.readyState,
                    id: t.id,
                }));
            }
        }
    } catch (_e) {
        /* noop */
    }
    return {
        muted: !!audioEl.muted,
        paused: !!audioEl.paused,
        volume: typeof audioEl.volume === "number" ? audioEl.volume : null,
        current_time:
            typeof audioEl.currentTime === "number"
                ? audioEl.currentTime
                : null,
        ready_state:
            typeof audioEl.readyState === "number"
                ? audioEl.readyState
                : null,
        src_object_kind: srcObjectKind,
        remote_tracks: remoteTracks,
        audio_ctx_state: audioCtx?.state ?? null,
    };
}

/**
 * Rearm the playback pipeline for a new response. Safe to call at any
 * time — every step is idempotent and swallows errors. Emits a
 * `playback.rearmed` diagnostic ONLY when it actually changed state
 * (so we can distinguish watchdog-triggered recovery from routine
 * no-op calls).
 *
 * IMPORTANT — this helper does NOT touch `.muted`. Unmuting is the
 * exclusive job of `releaseMuteForNewResponse`, which knows the user's
 * saved speaker preference via the `preInterruptMuted` snapshot. If
 * we unmuted here we'd fight the user's manual speaker-off toggle.
 * The audibility guardian for a "still muted from a previous
 * interrupt" scenario is the SAME multi-signal release path — call
 * `releaseMuteForNewResponse` for that.
 *
 * This is the "playback guardian" — Founder's directive after the
 * 2026-08-18 audibility regression: rearm playback on any evidence of
 * a new response (response.created OR first audio event OR track
 * activity) so a paused pipeline recovers.
 *
 * @param {object} deps
 * @param {HTMLAudioElement | null} deps.audioEl
 * @param {AudioContext | null} [deps.audioCtx]
 * @param {(event: object) => void} [deps.pushDiag]
 * @param {string} [deps.reason] - Diagnostic source label (e.g.
 *   "audio_start", "response.created", "watchdog").
 * @returns {{resumed_playback: boolean, resumed_ctx: boolean}}
 */
export function rearmPlayback({
    audioEl,
    audioCtx = null,
    pushDiag = null,
    reason = "unknown",
} = {}) {
    const result = {
        resumed_playback: false,
        resumed_ctx: false,
    };
    if (!audioEl) return result;

    // 1) Force resume playback. `.play()` on an already-playing
    //    element is a no-op; on a paused MediaStream-backed element
    //    it rearms consumption of WebRTC frames. Returns a promise;
    //    we don't await it (audibility must not block the event
    //    loop) but we DO catch its rejection so an autoplay-policy
    //    NotAllowedError doesn't surface as an unhandled rejection.
    try {
        if (audioEl.paused) {
            const p = audioEl.play && audioEl.play();
            if (p && typeof p.catch === "function") {
                p.catch(() => {
                    /* autoplay policy or transient — recorded below */
                });
            }
            result.resumed_playback = true;
        }
    } catch (_e) {
        /* noop */
    }

    // 2) Resume the AudioContext if it's suspended. This is the
    //    analyser graph feeding the orb visualizer; without it the
    //    orb goes idle even though audio plays. Independent of
    //    audibility but part of the same "make the pipeline live"
    //    contract.
    try {
        if (audioCtx && audioCtx.state === "suspended") {
            const p = audioCtx.resume && audioCtx.resume();
            if (p && typeof p.catch === "function") {
                p.catch(() => {});
            }
            result.resumed_ctx = true;
        }
    } catch (_e) {
        /* noop */
    }

    if (
        pushDiag &&
        (result.resumed_playback || result.resumed_ctx)
    ) {
        try {
            pushDiag({
                type: "playback.rearmed",
                detail: {
                    reason,
                    ...result,
                    pipeline: snapshotAudioPipeline(audioEl, audioCtx),
                },
            });
        } catch (_e) {
            /* noop */
        }
    }
    return result;
}

/**
 * Build a controller instance. Accepts thunks so the controller stays
 * decoupled from the specific refs held by the parent hook — the
 * refs may be swapped between sessions without invalidating the
 * controller.
 *
 * @param {object} deps
 * @param {() => (any | null)} deps.getSession - Returns the current
 *   @openai/agents-realtime RealtimeSession, or null when disconnected.
 * @param {() => (HTMLAudioElement | null)} deps.getAudioEl - Returns
 *   the audio element the SDK is piping model audio into, or null.
 * @param {() => (AudioContext | null)} [deps.getAudioCtx] - Optional.
 *   Returns the analyser AudioContext so release can resume it.
 * @param {(event: {type: string, response_id?: string | null, detail?: object}) => void} deps.pushDiag
 *   - Diagnostic sink (typically the parent hook's pushDiag helper).
 * @returns {InterruptionController}
 */
export function createInterruptionController({
    getSession,
    getAudioEl,
    getAudioCtx = () => null,
    pushDiag,
}) {
    // Pre-interrupt .muted snapshot. Null when we haven't muted. When
    // non-null, `releaseMuteForNewResponse` restores this exact value.
    let preInterruptMuted = /** @type {boolean | null} */ (null);

    function interrupt({
        reason,
        sdkAlreadyInterrupted = false,
        activeResponseId = null,
        prevAgentState = null,
        extra = {},
    } = {}) {
        const session = getSession && getSession();
        const audioEl = getAudioEl && getAudioEl();
        const audioCtx = getAudioCtx && getAudioCtx();

        // Step 2 — SDK primitive (skip when the SDK already fired it
        // for this event). Never let a throw abort the client-side
        // silence steps below.
        if (!sdkAlreadyInterrupted && session && typeof session.interrupt === "function") {
            try {
                session.interrupt();
            } catch (_e) {
                /* noop — controller must be resilient */
            }
        }

        // Step 3 — snapshot and force mute. HTMLMediaElement.muted is
        // synchronous; setting it true takes effect within the same
        // task. Save the prior value so we can restore the user's
        // real preference (e.g., speaker button) when the next
        // response starts.
        //
        // NOTE: We intentionally do NOT call `.pause()` here — see
        // the module docstring for the RCA. Pausing a MediaStream-
        // backed element decommissions the WebRTC playback pipeline
        // and browsers do not auto-resume when new frames arrive.
        // Mute alone gives us the audible silence we need while
        // leaving the pipeline armed for the next response.
        if (audioEl) {
            if (preInterruptMuted === null) {
                // Only snapshot on the first interrupt of a chain —
                // repeated interrupts within the same response should
                // keep the user's original preference recoverable.
                preInterruptMuted = !!audioEl.muted;
            }
            try {
                audioEl.muted = true;
            } catch (_e) {
                /* noop */
            }
        }

        // Step 4 — canonical diagnostic. One event shape, every trigger.
        // Includes a pipeline snapshot so the Founder-visible diagnostic
        // export shows the audibility state at the moment of interrupt.
        try {
            pushDiag &&
                pushDiag({
                    type: "session.interrupted",
                    response_id: activeResponseId,
                    detail: {
                        reason: reason || "unknown",
                        sdk_already_interrupted: !!sdkAlreadyInterrupted,
                        prev_agent_state: prevAgentState,
                        muted_by_interrupt: audioEl != null,
                        pipeline: snapshotAudioPipeline(audioEl, audioCtx),
                        ...extra,
                    },
                });
        } catch (_e) {
            /* noop */
        }
    }

    function isMutedByInterrupt() {
        return preInterruptMuted !== null;
    }

    function releaseMuteForNewResponse(newResponseId, options = {}) {
        const { onlyIfDifferent = null, source = "response.created" } =
            options;
        if (onlyIfDifferent && newResponseId === onlyIfDifferent) {
            // Same response id — this is the interrupted one, not a
            // new one. Wait for the next.
            return;
        }
        const audioEl = getAudioEl && getAudioEl();
        const audioCtx = getAudioCtx && getAudioCtx();

        // Restore the pre-interrupt .muted snapshot IF we are holding
        // one. If we are not (release fired via audio_start before any
        // interrupt this cycle), we still run the rearm-playback path
        // below defensively.
        let restoredMuted = null;
        if (preInterruptMuted !== null && audioEl) {
            try {
                audioEl.muted = preInterruptMuted;
                restoredMuted = preInterruptMuted;
            } catch (_e) {
                /* noop */
            }
            preInterruptMuted = null;
        }

        // Fail-safe rearm — if anything in the pipeline is dark, wake
        // it. `.play()` is idempotent for an already-playing element,
        // and unmute is idempotent for an already-audible one. This is
        // the belt-and-suspenders against a Founder-observed 2026-08-18
        // regression: audio element was `.paused=true` from a previous
        // (now-removed) `.pause()` call so subsequent responses were
        // inaudible despite `.muted=false`.
        const rearm = rearmPlayback({
            audioEl,
            audioCtx,
            pushDiag,
            reason: source || "release",
        });

        if (restoredMuted !== null || rearm.resumed_playback) {
            try {
                pushDiag &&
                    pushDiag({
                        type: "session.interrupt_released",
                        response_id: newResponseId,
                        detail: {
                            restored_muted: restoredMuted,
                            source,
                            rearm,
                            pipeline: snapshotAudioPipeline(
                                audioEl,
                                audioCtx,
                            ),
                        },
                    });
            } catch (_e) {
                /* noop */
            }
        }
    }

    function reset() {
        preInterruptMuted = null;
    }

    return {
        interrupt,
        isMutedByInterrupt,
        releaseMuteForNewResponse,
        reset,
    };
}

export default createInterruptionController;
